import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Clock, 
  Target, 
  Brain,
  Zap,
  Activity,
  AlertCircle,
  FileText,
  Users,
  Download,
  Filter
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer, 
  Legend,
  ScatterChart,
  Scatter,
  RadialBarChart,
  RadialBar
} from 'recharts';
import { useBills } from '@/hooks/useBills';
import { useDashboard } from '@/hooks/useDashboard';

interface AdvancedMetrics {
  processingEfficiency: number;
  aiAccuracy: number;
  throughputTrend: number;
  errorReduction: number;
  costSavings: number;
  automationRate: number;
  customerSatisfaction: number;
  systemUptime: number;
}

interface ProcessingTrend {
  date: string;
  volume: number;
  accuracy: number;
  speed: number;
  errors: number;
  cost: number;
}

interface SupplierPerformance {
  name: string;
  accuracy: number;
  volume: number;
  avgTime: number;
  errorRate: number;
  satisfaction: number;
}

interface AIModelComparison {
  model: string;
  accuracy: number;
  speed: number;
  cost: number;
  usage: number;
}

export const AdvancedAnalyticsDashboard: React.FC = () => {
  const { bills } = useBills();
  const { stats } = useDashboard();
  const [timeRange, setTimeRange] = useState('7days');
  const [metricType, setMetricType] = useState('performance');

  // Mock advanced metrics
  const metrics: AdvancedMetrics = {
    processingEfficiency: 87.5,
    aiAccuracy: 94.2,
    throughputTrend: 23.5,
    errorReduction: 67.8,
    costSavings: 45.3,
    automationRate: 89.7,
    customerSatisfaction: 92.1,
    systemUptime: 99.8
  };

  // Mock processing trends data
  const processingTrends: ProcessingTrend[] = [
    { date: '2024-01-19', volume: 245, accuracy: 92.1, speed: 3.2, errors: 12, cost: 234.56 },
    { date: '2024-01-20', volume: 278, accuracy: 93.5, speed: 3.0, errors: 8, cost: 198.45 },
    { date: '2024-01-21', volume: 312, accuracy: 94.2, speed: 2.8, errors: 6, cost: 187.23 },
    { date: '2024-01-22', volume: 289, accuracy: 95.1, speed: 2.9, errors: 4, cost: 176.89 },
    { date: '2024-01-23', volume: 356, accuracy: 94.8, speed: 2.7, errors: 7, cost: 201.34 },
    { date: '2024-01-24', volume: 298, accuracy: 96.2, speed: 2.6, errors: 3, cost: 165.78 },
    { date: '2024-01-25', volume: 334, accuracy: 95.7, speed: 2.5, errors: 5, cost: 178.92 }
  ];

  // Mock supplier performance data
  const supplierPerformance: SupplierPerformance[] = [
    { name: 'PG&E', accuracy: 96.2, volume: 145, avgTime: 2.3, errorRate: 3.8, satisfaction: 94.5 },
    { name: 'Edison', accuracy: 94.7, volume: 112, avgTime: 2.7, errorRate: 5.3, satisfaction: 91.2 },
    { name: 'ConEd', accuracy: 92.1, volume: 89, avgTime: 3.1, errorRate: 7.9, satisfaction: 87.8 },
    { name: 'ComEd', accuracy: 93.8, volume: 67, avgTime: 2.9, errorRate: 6.2, satisfaction: 89.4 },
    { name: 'Xcel Energy', accuracy: 91.5, volume: 43, avgTime: 3.4, errorRate: 8.5, satisfaction: 85.7 }
  ];

  // Mock AI model comparison data
  const aiModelComparison: AIModelComparison[] = [
    { model: 'GPT-4o', accuracy: 95.2, speed: 2.1, cost: 0.12, usage: 45 },
    { model: 'Claude-3', accuracy: 94.8, speed: 2.3, cost: 0.08, usage: 35 },
    { model: 'Gemini Pro', accuracy: 92.7, speed: 1.9, cost: 0.06, usage: 20 }
  ];

  const chartConfig = {
    volume: { label: "Volume", color: "hsl(var(--chart-1))" },
    accuracy: { label: "Accuracy %", color: "hsl(var(--chart-2))" },
    speed: { label: "Speed (s)", color: "hsl(var(--chart-3))" },
    errors: { label: "Errors", color: "hsl(var(--chart-4))" },
    cost: { label: "Cost ($)", color: "hsl(var(--chart-5))" },
  };

  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1', '#d084d0'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Advanced Analytics Dashboard</h2>
          <p className="text-muted-foreground">
            Phase 6: Enterprise-grade analytics with predictive insights and performance optimization
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24hours">24 Hours</SelectItem>
              <SelectItem value="7days">7 Days</SelectItem>
              <SelectItem value="30days">30 Days</SelectItem>
              <SelectItem value="90days">90 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Processing Efficiency</CardTitle>
            <Target className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{metrics.processingEfficiency}%</div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <span className="text-green-500">+12.3% from last period</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">AI Accuracy</CardTitle>
            <Brain className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-accent">{metrics.aiAccuracy}%</div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <span className="text-green-500">+2.8% improvement</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cost Savings</CardTitle>
            <DollarSign className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{metrics.costSavings}%</div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <span className="text-green-500">$23,450 saved</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Uptime</CardTitle>
            <Activity className="h-4 w-4 text-secondary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-secondary">{metrics.systemUptime}%</div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <span className="text-green-500">99.9% SLA target</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Advanced Analytics Tabs */}
      <Tabs defaultValue="performance" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="ai-models">AI Models</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="predictions">Predictions</TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Processing Volume & Accuracy</CardTitle>
                <CardDescription>
                  Daily processing metrics and accuracy trends
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={processingTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                      <YAxis yAxisId="volume" orientation="left" />
                      <YAxis yAxisId="accuracy" orientation="right" domain={[90, 100]} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Area 
                        yAxisId="volume"
                        type="monotone" 
                        dataKey="volume" 
                        stackId="1" 
                        stroke={chartConfig.volume.color} 
                        fill={chartConfig.volume.color} 
                        fillOpacity={0.6} 
                      />
                      <Line 
                        yAxisId="accuracy"
                        type="monotone" 
                        dataKey="accuracy" 
                        stroke={chartConfig.accuracy.color} 
                        strokeWidth={3}
                        dot={{ fill: chartConfig.accuracy.color, strokeWidth: 2, r: 4 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Processing Speed & Errors</CardTitle>
                <CardDescription>
                  Speed optimization and error reduction analysis
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={processingTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                      <YAxis yAxisId="speed" orientation="left" />
                      <YAxis yAxisId="errors" orientation="right" />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Line 
                        yAxisId="speed"
                        type="monotone" 
                        dataKey="speed" 
                        stroke={chartConfig.speed.color} 
                        strokeWidth={3}
                        dot={{ fill: chartConfig.speed.color, strokeWidth: 2, r: 4 }}
                      />
                      <Line 
                        yAxisId="errors"
                        type="monotone" 
                        dataKey="errors" 
                        stroke={chartConfig.errors.color} 
                        strokeWidth={3}
                        dot={{ fill: chartConfig.errors.color, strokeWidth: 2, r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Performance Heatmap</CardTitle>
              <CardDescription>
                Multi-dimensional performance analysis across key metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 rounded-lg bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/20 dark:to-green-900/20">
                  <div className="text-2xl font-bold text-green-700 dark:text-green-300">{metrics.processingEfficiency}%</div>
                  <p className="text-sm text-green-600 dark:text-green-400">Processing Efficiency</p>
                  <Badge variant="secondary" className="mt-1">Excellent</Badge>
                </div>
                <div className="text-center p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20">
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{metrics.automationRate}%</div>
                  <p className="text-sm text-blue-600 dark:text-blue-400">Automation Rate</p>
                  <Badge variant="secondary" className="mt-1">High</Badge>
                </div>
                <div className="text-center p-4 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/20 dark:to-purple-900/20">
                  <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">{metrics.errorReduction}%</div>
                  <p className="text-sm text-purple-600 dark:text-purple-400">Error Reduction</p>
                  <Badge variant="secondary" className="mt-1">Significant</Badge>
                </div>
                <div className="text-center p-4 rounded-lg bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950/20 dark:to-orange-900/20">
                  <div className="text-2xl font-bold text-orange-700 dark:text-orange-300">{metrics.customerSatisfaction}%</div>
                  <p className="text-sm text-orange-600 dark:text-orange-400">Customer Satisfaction</p>
                  <Badge variant="secondary" className="mt-1">Outstanding</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suppliers" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Supplier Performance Comparison</CardTitle>
                <CardDescription>
                  Accuracy and volume metrics by utility provider
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={supplierPerformance}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis yAxisId="accuracy" orientation="left" domain={[85, 100]} />
                      <YAxis yAxisId="volume" orientation="right" />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar yAxisId="accuracy" dataKey="accuracy" fill={colors[0]} />
                      <Bar yAxisId="volume" dataKey="volume" fill={colors[1]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Processing Time Distribution</CardTitle>
                <CardDescription>
                  Average processing time by supplier
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Pie 
                        data={supplierPerformance} 
                        dataKey="avgTime" 
                        cx="50%" 
                        cy="50%" 
                        outerRadius={80}
                        label={(entry) => entry.name}
                      >
                        {supplierPerformance.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Supplier Performance Matrix</CardTitle>
              <CardDescription>
                Detailed performance metrics and satisfaction scores
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {supplierPerformance.map((supplier, index) => (
                  <div key={supplier.name} className="grid grid-cols-6 gap-4 p-4 rounded-lg border">
                    <div>
                      <p className="font-medium">{supplier.name}</p>
                      <p className="text-sm text-muted-foreground">{supplier.volume} bills</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-green-600">{supplier.accuracy}%</p>
                      <p className="text-xs text-muted-foreground">Accuracy</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-blue-600">{supplier.avgTime}s</p>
                      <p className="text-xs text-muted-foreground">Avg Time</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-red-600">{supplier.errorRate}%</p>
                      <p className="text-xs text-muted-foreground">Error Rate</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-purple-600">{supplier.satisfaction}%</p>
                      <p className="text-xs text-muted-foreground">Satisfaction</p>
                    </div>
                    <div className="flex items-center justify-center">
                      <Badge variant={supplier.accuracy >= 95 ? 'default' : supplier.accuracy >= 90 ? 'secondary' : 'outline'}>
                        {supplier.accuracy >= 95 ? 'Excellent' : supplier.accuracy >= 90 ? 'Good' : 'Needs Improvement'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai-models" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>AI Model Performance</CardTitle>
                <CardDescription>
                  Accuracy vs Speed comparison across AI models
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart data={aiModelComparison}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="speed" name="Speed (s)" />
                      <YAxis dataKey="accuracy" name="Accuracy %" domain={[90, 100]} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Scatter dataKey="accuracy" fill={colors[0]} />
                    </ScatterChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Model Usage Distribution</CardTitle>
                <CardDescription>
                  Current usage allocation across AI models
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart data={aiModelComparison} cx="50%" cy="50%" innerRadius="20%" outerRadius="80%">
                      <RadialBar dataKey="usage" cornerRadius={10} fill={colors[0]} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>AI Model Comparison Matrix</CardTitle>
              <CardDescription>
                Comprehensive comparison of AI model performance metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {aiModelComparison.map((model, index) => (
                  <div key={model.model} className="grid grid-cols-5 gap-4 p-4 rounded-lg border">
                    <div>
                      <p className="font-medium">{model.model}</p>
                      <Badge variant="outline" className="mt-1">{model.usage}% usage</Badge>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-green-600">{model.accuracy}%</p>
                      <p className="text-xs text-muted-foreground">Accuracy</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-blue-600">{model.speed}s</p>
                      <p className="text-xs text-muted-foreground">Speed</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-orange-600">${model.cost}</p>
                      <p className="text-xs text-muted-foreground">Cost per 1K</p>
                    </div>
                    <div className="flex items-center justify-center">
                      <Badge variant={model.accuracy >= 95 ? 'default' : 'secondary'}>
                        {model.accuracy >= 95 ? 'Recommended' : 'Standard'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Multi-Metric Trend Analysis</CardTitle>
              <CardDescription>
                Comprehensive view of all key performance indicators over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={processingTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="accuracy" 
                      stroke={chartConfig.accuracy.color} 
                      strokeWidth={2}
                      name="Accuracy %"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="speed" 
                      stroke={chartConfig.speed.color} 
                      strokeWidth={2}
                      name="Speed (s)"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="errors" 
                      stroke={chartConfig.errors.color} 
                      strokeWidth={2}
                      name="Errors"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Cost Efficiency Trends</CardTitle>
              <CardDescription>
                Processing costs and volume relationship analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={processingTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                    <YAxis yAxisId="cost" orientation="left" />
                    <YAxis yAxisId="volume" orientation="right" />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Area 
                      yAxisId="cost"
                      type="monotone" 
                      dataKey="cost" 
                      stackId="1" 
                      stroke={chartConfig.cost.color} 
                      fill={chartConfig.cost.color} 
                      fillOpacity={0.6} 
                    />
                    <Line 
                      yAxisId="volume"
                      type="monotone" 
                      dataKey="volume" 
                      stroke={chartConfig.volume.color} 
                      strokeWidth={3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="predictions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                Predictive Analytics & Forecasting
              </CardTitle>
              <CardDescription>
                AI-powered predictions and optimization recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium">Volume Predictions</h4>
                  <div className="space-y-3">
                    <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20">
                      <h5 className="font-medium text-blue-900 dark:text-blue-100">Next 7 Days</h5>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        Expected processing volume: 2,450 bills (±150)
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <TrendingUp className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-600">+15% increase predicted</span>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/20">
                      <h5 className="font-medium text-green-900 dark:text-green-100">Peak Hours</h5>
                      <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                        Predicted peak: 10 AM - 2 PM (45% of daily volume)
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Clock className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-600">Schedule maintenance at 3 AM</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium">Performance Predictions</h4>
                  <div className="space-y-3">
                    <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950/20">
                      <h5 className="font-medium text-purple-900 dark:text-purple-100">Accuracy Forecast</h5>
                      <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                        Projected accuracy: 96.2% (current: 94.2%)
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Target className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium text-purple-600">Model improvements incoming</span>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-950/20">
                      <h5 className="font-medium text-orange-900 dark:text-orange-100">Resource Requirements</h5>
                      <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                        Scale up by 20% during peak hours for optimal performance
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Zap className="h-4 w-4 text-orange-600" />
                        <span className="text-sm font-medium text-orange-600">Auto-scaling recommended</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Optimization Recommendations</CardTitle>
              <CardDescription>
                AI-powered suggestions for improving system performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Badge variant="outline" className="mt-1">1</Badge>
                  <div className="flex-1">
                    <p className="font-medium">Implement Smart Batching</p>
                    <p className="text-sm text-muted-foreground">
                      Group similar document types for 25% faster processing. Predicted impact: +2.3% accuracy, -15% processing time.
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary">High Impact</Badge>
                      <Badge variant="outline">Low Effort</Badge>
                    </div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Badge variant="outline" className="mt-1">2</Badge>
                  <div className="flex-1">
                    <p className="font-medium">Upgrade AI Model Mix</p>
                    <p className="text-sm text-muted-foreground">
                      Adjust model allocation: 60% GPT-4o, 30% Claude-3, 10% Gemini. Estimated cost reduction: 12%.
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary">Medium Impact</Badge>
                      <Badge variant="outline">Medium Effort</Badge>
                    </div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Badge variant="outline" className="mt-1">3</Badge>
                  <div className="flex-1">
                    <p className="font-medium">Optimize Supplier-Specific Processing</p>
                    <p className="text-sm text-muted-foreground">
                      Create specialized pipelines for top 3 suppliers. Expected accuracy improvement: +4.7%.
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary">High Impact</Badge>
                      <Badge variant="outline">High Effort</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};